/* eslint-disable lingui/no-unlocalized-strings -- contratos técnicos HTTP */
import { TIMEOUTS } from '@/shared/http/apis';
import { creditApi } from '@/shared/http/client';

import type { BankAccountBody } from './person-bank-account';
import type { FraudRecordBody } from './person-fraud.form';
import type { PersonResource } from './person.kind';
import { wire, withSignal } from '@/shared/reference/wire';
import {
  bankAccountIdsSchema,
  economicActivityCodeListSchema,
  legalPersonListPageSchema,
  legalPersonOptionPageSchema,
  legalPersonSchema,
  naturalPersonListPageSchema,
  naturalPersonSchema,
  personOptionPageSchema,
  personSaveResultSchema,
  datasetListSchema,
  queryBasicDataSchema,
  queryListSchema,
  type ComplianceChecklistCreate,
} from './person.wire';
export async function listLegalPersonOptions(
  options: { searchString?: string | undefined; tenant: string | undefined },
  signal?: AbortSignal,
) {
  const params = { searchString: options.searchString, page: 0, size: 15, tenant: options.tenant };
  const data = await creditApi.get<unknown>('/LegalPerson', { params, ...withSignal(signal) });
  return wire(legalPersonOptionPageSchema, data, 'GET /LegalPerson');
}
export async function listPersonOptions(
  options: { searchString?: string | undefined; tenant: string | undefined },
  signal?: AbortSignal,
) {
  const params = { page: 0, size: 10, tenant: options.tenant, searchString: options.searchString };
  const data = await creditApi.get<unknown>('/Person', { params, ...withSignal(signal) });
  return wire(personOptionPageSchema, data, 'GET /Person');
}
/** UYZY services/accounts/naturalPerson/naturalPerson.ts:47; naturalPersonContext.tsx:110. */
export async function listNaturalPersonOptions(
  options: { tenant: string | undefined; searchString?: string | undefined },
  signal?: AbortSignal,
) {
  const params = { tenant: options.tenant, page: 0, size: 15, searchString: options.searchString };
  return wire(
    personOptionPageSchema,
    await creditApi.get<unknown>('/NaturalPerson', { params, ...withSignal(signal) }),
    'GET /NaturalPerson',
  );
}

// ─── Listas e exclusão (inventário §2.4 e §2.5) ─────────────────────────────

export interface PersonListParams {
  readonly page: number;
  readonly size: number;
  readonly pendingPreApprove: boolean;
  /** Filtros já no formato do fio, na ordem em que a pessoa aplicou (`filterContext.tsx:236-281`). */
  readonly filters: Readonly<Record<string, unknown>>;
  /** Correspondente da casca (`currentTenantId`). */
  readonly tenant: string | undefined;
}

/**
 * `getNaturalPersonsList` (naturalPerson.ts:47-54) via `useNaturalPersonList` (naturalPersonContext.tsx:110):
 * `{ tenant, page, size, pendingPreApprove, ...filtros }`; o filtro Correspondente troca o tenant no lugar.
 */
export async function listNaturalPersons(params: PersonListParams, signal?: AbortSignal) {
  const query = { tenant: params.tenant, page: params.page, size: params.size, pendingPreApprove: params.pendingPreApprove, ...params.filters };
  return wire(naturalPersonListPageSchema, await creditApi.get<unknown>('/NaturalPerson', { params: query, ...withSignal(signal) }), 'GET /NaturalPerson');
}

/**
 * `getLegalPersonsList` (LegalPerson.ts:49-56) via `useLegalPersonListData` (legalPersonContext.tsx:118):
 * `{ ...filtros, page, size, pendingPreApprove, tenant }` — o tenant vai por último.
 */
export async function listLegalPersons(params: PersonListParams, signal?: AbortSignal) {
  const query = { ...params.filters, page: params.page, size: params.size, pendingPreApprove: params.pendingPreApprove, tenant: params.tenant };
  return wire(legalPersonListPageSchema, await creditApi.get<unknown>('/LegalPerson', { params: query, ...withSignal(signal) }), 'GET /LegalPerson');
}

/** `deleteNaturalPersonById` (naturalPerson.ts:141-146): sem query. */
export async function deleteNaturalPerson(id: string) {
  await creditApi.delete(`/NaturalPerson/${id}`);
}

/** `deleteLegalPersonById` (LegalPerson.ts:73-80): o `tenant` é sempre indefinido na lista, então não vai. */
export async function deleteLegalPerson(id: string) {
  await creditApi.delete(`/LegalPerson/${id}`);
}

// ─── Registro (inventário §3) ───────────────────────────────────────────────

/** `getNaturalPersonById` (naturalPerson.ts:18-24): sem query. */
export async function getNaturalPerson(id: string, signal?: AbortSignal) {
  return wire(naturalPersonSchema, await creditApi.get<unknown>(`/NaturalPerson/${id}`, withSignal(signal)), 'GET /NaturalPerson/{id}');
}

/** `getLegalPersonById` (LegalPerson.ts:14-20): sem query. */
export async function getLegalPerson(id: string, signal?: AbortSignal) {
  return wire(legalPersonSchema, await creditApi.get<unknown>(`/LegalPerson/${id}`, withSignal(signal)), 'GET /LegalPerson/{id}');
}

/**
 * `editOrCreateNaturalPerson` (naturalPerson.ts:70-139) via `useNaturalPersonMutation` (naturalPersonContext.tsx:268-300):
 * `POST /NaturalPerson` ou `PUT /NaturalPerson/{id}`, com `tenant` (correspondente da casca) e `returnValue=Id`.
 * O corpo já vem pronto do mapper (`person.form.ts`).
 */
export async function saveNaturalPerson(input: { id: string | undefined; body: Readonly<Record<string, unknown>>; tenant: string | undefined }) {
  const config = { params: { tenant: input.tenant, returnValue: 'Id' } };
  const data = input.id === undefined ? await creditApi.post('/NaturalPerson', input.body, config) : await creditApi.put<unknown>(`/NaturalPerson/${input.id}`, input.body, config);
  return wire(personSaveResultSchema, data, 'POST|PUT /NaturalPerson');
}

/** `editOrCreateLegalPerson` (LegalPerson.ts:23-47) via `useLegalPersonMutation` (legalPersonContext.tsx:61-80): só `tenant`. */
export async function saveLegalPerson(input: { id: string | undefined; body: Readonly<Record<string, unknown>>; tenant: string | undefined }) {
  const config = { params: { tenant: input.tenant } };
  const data = input.id === undefined ? await creditApi.post('/LegalPerson', input.body, config) : await creditApi.put<unknown>(`/LegalPerson/${input.id}`, input.body, config);
  return wire(personSaveResultSchema, data, 'POST|PUT /LegalPerson');
}

/**
 * Clonar: `createNaturalPerson` / `createLegalPerson` (naturalPerson.ts:148-160, LegalPerson.ts:58-71) com o
 * correspondente escolhido na query; o corpo é o `toDataModel` do registro cru (sem os casts do formulário).
 */
export async function clonePerson(resource: 'NaturalPerson' | 'LegalPerson', body: Readonly<Record<string, unknown>>, tenant: string) {
  return wire(personSaveResultSchema, await creditApi.post(`/${resource}`, body, { params: { tenant } }), `POST /${resource} (clonar)`);
}

/**
 * `assign` (services/assign/index.ts:6-25): `POST /{Recurso}/{id}/assign`, corpo pelo `AssingCreateModel`
 * (`userId`, `groupName`, `tenant`); o campo não escolhido vai `""` e o `tenant` sem escolha some (F3 de `fund`).
 */
export async function assignPerson(resource: 'NaturalPerson' | 'LegalPerson', id: string, input: { userId: string | null; groupName: string | null; tenant: string }) {
  await creditApi.post(`/${resource}/${id}/assign`, { userId: input.userId ?? '', groupName: input.groupName ?? '', tenant: input.tenant || undefined });
}

/** `postRegulateRegistrationNumber` (naturalPerson.ts:179-184) via "Regularizar CPF": corpo `{}`, sem query nem tenant. */
export async function regulateRegistrationNumber(id: string) {
  await creditApi.post(`/NaturalPerson/${id}/EnableRegistrationNumber`, {});
}

/**
 * CNAE (`useEconomicActivityCodeListData`, economicActivityCode.tsx:8-50): com 3 letras ou mais,
 * `GET /EconomicActivityCode/{busca}`; com menos, `GET /EconomicActivityCode`.
 */
export async function listEconomicActivityCodes(search: string, signal?: AbortSignal) {
  const path = search.length >= 3 ? `/EconomicActivityCode/${search}` : '/EconomicActivityCode';
  return wire(economicActivityCodeListSchema, (await creditApi.get<unknown>(path, withSignal(signal))) ?? [], 'GET /EconomicActivityCode');
}

/**
 * Gestor da empresa (PJ): `useNaturalPersonList({ page: 0, size: 10 })` (InformationTabContainer.tsx:30-33) —
 * `tenant, page, size` e a busca por último (`setFilterValues({ searchString })`).
 */
export async function listManagerOptions(options: { tenant: string | undefined; searchString?: string | undefined }, signal?: AbortSignal) {
  const params = { tenant: options.tenant, page: 0, size: 10, searchString: options.searchString };
  return wire(personOptionPageSchema, await creditApi.get<unknown>('/NaturalPerson', { params, ...withSignal(signal) }), 'GET /NaturalPerson (gestor)');
}

// ─── Datasets de bureau (inventário §4) ─────────────────────────────────────

/** `getDatasetTimeout` (contexts/datasetContext.tsx:28-34): 90 s; 2 min para antecedente e ondemand. */
export function datasetTimeout(dataset: string): number {
  const name = dataset.toLowerCase();
  return name.includes('antecedente') || name.includes('ondemand') ? TIMEOUTS.min2 : TIMEOUTS.s90;
}

/**
 * `getDataset` (services/datasets/datasets.ts:15-26): a leitura do que já foi consultado, **sem custo**.
 * `params` é a query que o legado escreve no nome (`ClearSaleR6Fraud?getOnlyMostRecent=false`).
 */
export async function getPersonDataset(resource: PersonResource, id: string, dataset: string, signal?: AbortSignal, params?: Readonly<Record<string, string | number | boolean>>) {
  const data = await creditApi.get<unknown>(`/${resource}/${id}/Dataset/${dataset}`, { ...withSignal(signal), ...(params ? { params } : {}), timeout: datasetTimeout(dataset) });
  return wire(datasetListSchema, data ?? [], 'GET /{Pessoa}/{id}/Dataset/{nome}');
}

/** `postDataset` (services/datasets/datasets.ts:5-13): **consulta paga ao bureau**. Só por ação humana, com prévia (X01). */
export async function requestPersonDataset(resource: PersonResource, id: string, dataset: string) {
  return creditApi.post(`/${resource}/${id}/Dataset/${dataset}`, {}, { timeout: datasetTimeout(dataset) });
}

/** `getDatasetOnlyTokensByPersonID` (services/datasets/datasets.ts:28-36): tokens de averbação, `getOnlyMostRecent=false`. */
export async function listReserveTokenDataset(personId: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/NaturalPerson/${personId}/dataset/ReserveToken`, { ...withSignal(signal), params: { getOnlyMostRecent: false } });
  return wire(queryListSchema, data ?? [], 'GET /NaturalPerson/{id}/dataset/ReserveToken');
}

// ─── Ações do registro (unidade 6A) ─────────────────────────────────────────

/**
 * `getPersonsByRegistrationNumberAsync` (services/person/personService.ts:17-27) via "Carregar informações"
 * (`handleSubmitQueryData`, helpers/methods/queryBasicDataPerson.ts:88-101): `GET /Person/{documento}/QueryBasicData`,
 * sem query nem tenant. **Consulta ao bureau**, nunca pelo agente (X01). Em Pessoas sai pelo clique em Continuar do
 * formulário; no "Adicionar favorecido" de Contas digitais sai sozinha quando o documento fica completo, como o legado
 * (`BankAccountFavoredForm.tsx:89-98`). Se for consulta paga, o favorecido passa a ser por clique (Q3 da U2a).
 */
export async function queryBasicData(registrationNumber: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/Person/${registrationNumber}/QueryBasicData`, withSignal(signal));
  return wire(queryBasicDataSchema, data, 'GET /Person/{documento}/QueryBasicData');
}

/**
 * `createComplianceCheckList` (services/creditNote/complianceChecklist/complianceChecklist.ts:23-29) via
 * "Incluir na Blacklist" (`BlackListContainer.tsx:54-67`): a lista no corpo, sem query nem tenant.
 */
export async function createComplianceChecklist(items: readonly ComplianceChecklistCreate[]) {
  await creditApi.post('/complianceChecklist', items);
}

/**
 * `postFraudRecordAsync` (services/fraudRecord/FraudRecord.tsx:6-15) via `useFraudRecordMutation`
 * (contexts/fraudRecordContext.tsx:8-37): `POST /ClearSaleR6/RegisterFraud?personId={id}`, sem tenant, no timeout
 * padrão da CreditApi. O corpo vem pronto de `fraudRecordBody` (`toDataModel(FraudRecordCreateModel)`). O legado
 * não lê a resposta: nada é validado nem devolvido.
 */
export async function registerFraud(personId: string, body: FraudRecordBody) {
  await creditApi.post('/ClearSaleR6/RegisterFraud', body, { params: { personId } });
}

// ─── Dados bancários (unidade 10E) ──────────────────────────────────────────

/**
 * `saveOrUpdateBankAccountAsync` (services/person/personService.ts:29-53) via `usePersonsBankAccountMutation`
 * (contexts/Persons/personsContext.tsx:16-66), conta nova: `POST /{Pessoa}/{id}/BankAccount` com a lista `[conta]`
 * (`BankAccountCreateModel`), sem query nem tenant, no timeout padrão. Devolve a lista de ids (`Array<string>`).
 */
export async function createPersonBankAccount(resource: PersonResource, personId: string, body: BankAccountBody) {
  return wire(bankAccountIdsSchema, await creditApi.post(`/${resource}/${personId}/BankAccount`, [body]), 'POST /{Pessoa}/{id}/BankAccount');
}

/** O mesmo serviço com a conta (`bankAccountId`): `PUT /{Pessoa}/{id}/BankAccount/{conta}` com o objeto (`BankAccountUpdateModel`). */
export async function updatePersonBankAccount(resource: PersonResource, personId: string, accountId: string, body: BankAccountBody) {
  await creditApi.put(`/${resource}/${personId}/BankAccount/${accountId}`, body);
}

/**
 * "Excluir" de conta gravada (`deletePersonsAccountById`, services/person/personService.ts:55-68, pelo
 * `useDeleteBankAccountMutation`, contexts/Persons/personsContext.tsx:75-113): `DELETE /{Pessoa}/{id}/BankAccount/{conta}`,
 * sem corpo, sem query nem tenant, no timeout padrão. R5.
 */
export async function deletePersonBankAccount(resource: PersonResource, personId: string, accountId: string) {
  await creditApi.delete(`/${resource}/${personId}/BankAccount/${accountId}`);
}

/**
 * "Reservar número de conta" do drawer de dados bancários da operação (`postOpenBankAccountByPersonId`,
 * naturalPerson.ts:186-192; OpenBankAccount.tsx:43-65): `POST /NaturalPerson/{id}/OpenBankAccount` com `{}`, sem query
 * nem tenant, timeout de 30 s. Devolve a conta aberta (`RelatedBankAccountType`) ou `null`.
 */
export async function openNaturalPersonBankAccount(personId: string): Promise<Readonly<Record<string, unknown>> | null> {
  const data = await creditApi.post(`/NaturalPerson/${personId}/OpenBankAccount`, {}, { timeout: 30_000 });
  return data !== null && typeof data === 'object' && !Array.isArray(data) ? (data as Readonly<Record<string, unknown>>) : null;
}
